/**
 * Monthly menu-calendar PDF — the school-facing "Print Calendar" export.
 *
 * Schools pick up to three months (the current one plus the next two) and get
 * ONE PDF with a page per month × per mealtime the school actually serves.
 * The month math is pure so the picker can be unit-tested without React.
 */

import { DEFAULT_ORDO_LOCALE, type OrdoLocale } from '@ordo/i18n'

import { apiFetchBlob, saveBlob } from './http'

/** How many months forward the picker offers, inclusive of the current one. */
export const MONTH_OPTION_COUNT = 3

export interface MonthOption {
  /** `YYYY-MM` — the value the endpoint takes. */
  value: string
  /** e.g. "August 2026". */
  label: string
}

/** `YYYY-MM` for a (year, 1-based month). */
function monthValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

/**
 * The current month plus the next two, in order. Derived from the viewer's
 * LOCAL wall clock (same rule as `todayKey` in school-orders/calendar.ts) so
 * "this month" matches the month the user believes they are in.
 */
export function buildMonthOptions(now: Date = new Date(), count: number = MONTH_OPTION_COUNT): MonthOption[] {
  const options: MonthOption[] = []
  for (let offset = 0; offset < count; offset += 1) {
    const year = now.getFullYear()
    const month = now.getMonth() + offset // 0-based, may overflow past 11
    const date = new Date(Date.UTC(year, month, 1))
    options.push({
      value: monthValue(date.getUTCFullYear(), date.getUTCMonth() + 1),
      label: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date),
    })
  }
  return options
}

/** Filename fallback when the response carries no Content-Disposition. */
export function fallbackCalendarFilename(schoolName: string, months: string[]): string {
  const span = months.length === 1 ? months[0] : `${months[0]} to ${months.at(-1)}`
  return `${schoolName} - Menu Calendar - ${span}.pdf`
    .replace(/[^\w \-.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The campuses a district calendar export covers: every campus of the
 * selected school's organization the admin holds, in the admin's order. A
 * single-campus admin (or a campus alone in its org) gets just that campus.
 */
export function districtCampuses<T extends { id: string; organizationId: string }>(
  school: T,
  schools: readonly T[] = [],
): T[] {
  const sameOrg = schools.filter((candidate) => candidate.organizationId === school.organizationId)
  return sameOrg.some((candidate) => candidate.id === school.id) ? sameOrg : [school, ...sameOrg]
}

/**
 * Download the calendar PDF for `months` at `locationId` — or, with more than
 * one `locationIds`, the district's calendar: the server prints one calendar
 * per distinct menu, labelled with the campuses it covers.
 *
 * Goes through `apiFetchBlob` rather than a link/`window.open` on purpose:
 * the impersonation + kiosk tokens are HEADERS, and a top-level navigation
 * can't carry them — an admin in "Access school view" would otherwise export
 * as themselves instead of as the school.
 */
export async function downloadMonthCalendarPdf(args: {
  locationId: string
  /** Every campus to print; more than one makes it a district export. */
  locationIds?: string[]
  schoolName: string
  months: string[]
  gradeRangeCode?: string | null
  /**
   * Language for the printed calendar (PROD-1293). An EXPLICIT choice, never
   * the viewer's own UI locale — a school admin exporting a Spanish calendar to
   * hand to families is choosing for the AUDIENCE, not for themselves. Omitted
   * or `en-US` ⇒ the parameter is not sent at all and the server's default
   * English path runs.
   */
  lang?: OrdoLocale
}): Promise<void> {
  if (args.months.length === 0) {
    throw new Error('Pick at least one month to print.')
  }
  const query: Record<string, string> =
    args.locationIds && args.locationIds.length > 1
      ? { locationIds: args.locationIds.join(','), months: args.months.join(',') }
      : { locationId: args.locationId, months: args.months.join(',') }
  if (args.gradeRangeCode) {
    query['gradeRangeCode'] = args.gradeRangeCode
  }
  if (args.lang && args.lang !== DEFAULT_ORDO_LOCALE) {
    query['lang'] = args.lang
  }
  const { blob, filename } = await apiFetchBlob('/api/menus/month-calendar.pdf', { query })
  saveBlob(blob, filename ?? fallbackCalendarFilename(args.schoolName, args.months))
}

/** The five compliance documents a school can pull down (PROD-1300). */
export type NutritionalAnalysisFormat =
  | 'compliance-calendar-pdf'
  | 'analysis-pdf'
  | 'analysis-csv'
  | 'carb-counts-pdf'
  | 'recipes-pdf'
  | 'cn-labels-zip'

const NUTRITIONAL_ANALYSIS_PATHS: Record<
  NutritionalAnalysisFormat,
  { path: string; extension: string; label: string }
> = {
  'compliance-calendar-pdf': {
    path: '/api/menus/compliance-calendar.pdf',
    extension: 'pdf',
    label: 'Compliance Calendar',
  },
  'analysis-pdf': { path: '/api/menus/nutritional-analysis.pdf', extension: 'pdf', label: 'Nutritional Analysis' },
  'analysis-csv': { path: '/api/menus/nutritional-analysis.csv', extension: 'csv', label: 'Nutritional Analysis' },
  'carb-counts-pdf': { path: '/api/menus/carb-counts.pdf', extension: 'pdf', label: 'Carb Counts' },
  'recipes-pdf': { path: '/api/menus/recipes.pdf', extension: 'pdf', label: 'Recipes' },
  'cn-labels-zip': { path: '/api/menus/nutritional-analysis/cn-labels.zip', extension: 'zip', label: 'CN Labels' },
}

/** Filename fallback when the response carries no Content-Disposition. */
export function fallbackNutritionalAnalysisFilename(
  schoolName: string,
  months: string[],
  format: NutritionalAnalysisFormat,
): string {
  const span = months.length === 1 ? months[0] : `${months[0]} to ${months.at(-1)}`
  const { label, extension } = NUTRITIONAL_ANALYSIS_PATHS[format]
  return `${schoolName} - ${label} - ${span}.${extension}`
    .replace(/[^\w \-.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Download the compliance-format "Nutritional Analysis" export for `months`
 * at `locationId` — per-component crediting, serving sizes, daily milk,
 * grade levels, allergens, substitutions, and the per-item nutrition table.
 * Same `apiFetchBlob` rationale as the calendar PDF (header-borne tokens).
 */
export async function downloadNutritionalAnalysis(args: {
  locationId: string
  schoolName: string
  months: string[]
  format: NutritionalAnalysisFormat
}): Promise<void> {
  if (args.months.length === 0) {
    throw new Error('Pick at least one month to export.')
  }
  const { blob, filename } = await apiFetchBlob(NUTRITIONAL_ANALYSIS_PATHS[args.format].path, {
    query: { locationId: args.locationId, months: args.months.join(',') },
  })
  saveBlob(blob, filename ?? fallbackNutritionalAnalysisFilename(args.schoolName, args.months, args.format))
}
